import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /venue-terms - Whistll Venue Partner Agreement (last updated April 2026).
 * Copy transcribed from ~/Documents/Whistll/whistll-all-documents/
 * whistll-venue-partner-agreement.pdf. Keep in sync when legal updates
 * anything. Cross-referenced from Terms.tsx and the venue onboarding
 * form's accept-terms checkbox.
 */
export function VenueTermsPage() {
  return (
    <LegalPlaceholder
      title="Venue Partner Agreement"
      lastUpdated="April 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        This Venue Partner Agreement ("Agreement") governs the
        relationship between Whistll ("we", "us", "our", or "Whistll")
        and venues, facility operators, and court owners ("you",
        "your", or "Venue Partner") who use Whistll to list courts,
        receive bookings, and receive payouts through the Whistll
        platform (the "Platform").
      </p>
      <p>
        By submitting a venue application or accepting bookings
        through Whistll, you agree to be bound by this Agreement. If
        you are entering into this Agreement on behalf of an
        organisation, you represent that you have the authority to do
        so.
      </p>
      <p>
        This Agreement is separate from, and in addition to, the{" "}
        <a
          href="/terms"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Whistll User Terms and Conditions
        </a>{" "}
        and{" "}
        <a
          href="/privacy-policy"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Privacy Policy
        </a>
        .
      </p>

      <h2>2. Whistll's Role</h2>
      <p>
        <strong>2.1 Platform Only.</strong> Whistll is a technology
        platform that connects basketball players with basketball
        courts. Whistll does not act as a booking agent, travel
        agent, or reseller. The contractual relationship for the
        court rental is between you (the Venue Partner) and the
        player.
      </p>
      <p>
        <strong>2.2 No Control Over Venues.</strong> Whistll does not
        own, operate, manage, or supervise any Venue Partner's
        facilities. You are solely responsible for the operation,
        maintenance, safety, and legal compliance of your facilities.
      </p>
      <p>
        <strong>2.3 Facilitation of Bookings.</strong> Whistll
        facilitates bookings by:
      </p>
      <ul>
        <li>(a) listing your courts and availability in the Platform;</li>
        <li>(b) collecting payment from players at the point of booking;</li>
        <li>
          (c) transferring the court fee to you once the game is
          confirmed; and
        </li>
        <li>
          (d) providing communication tools between you and players
          where applicable.
        </li>
      </ul>

      <h2>3. Venue Onboarding &amp; Approval</h2>
      <p>
        <strong>3.1 Application.</strong> To become a Venue Partner,
        you must complete the venue application at{" "}
        <a
          href="https://whistll.com/venues"
          className="underline underline-offset-2 hover:text-foreground"
        >
          whistll.com/venues
        </a>{" "}
        and provide accurate information including:
      </p>
      <ul>
        <li>(a) venue name, address, and contact details;</li>
        <li>(b) court details (number of courts, court type, surface);</li>
        <li>(c) pricing per player per hour;</li>
        <li>(d) booking system used (where applicable); and</li>
        <li>(e) court photos.</li>
      </ul>
      <p>
        <strong>3.2 Approval.</strong> Whistll reviews all venue
        applications and reserves the right to approve, reject, or
        request further information at its sole discretion. Approval
        is not guaranteed.
      </p>
      <p>
        <strong>3.3 Court Photos.</strong> All court photos submitted
        by Venue Partners are reviewed by Whistll before going live in
        the App. Whistll reserves the right to reject photos that are
        low quality, misleading, or do not represent the facility
        accurately.
      </p>
      <p>
        By submitting photos, you grant Whistll a non-exclusive,
        royalty-free, worldwide licence to host, store, reproduce,
        display, and distribute those photos within the App and for
        related marketing and promotional purposes relating to your
        venue.
      </p>
      <p>
        <strong>3.4 Stripe Connect.</strong> Venue Partners receive
        payouts through Stripe Connect. You must complete Stripe
        Connect onboarding and provide verified bank details before
        your courts can go live in the App. Your use of Stripe
        Connect is subject to Stripe's own terms.
      </p>
      <p>
        <strong>3.5 Activation.</strong> Your venue becomes live in
        the App only after:
      </p>
      <ul>
        <li>(a) application approval;</li>
        <li>(b) photo approval; and</li>
        <li>(c) Stripe Connect verification.</li>
      </ul>

      <h2>4. Availability &amp; Integration Tiers</h2>
      <p>
        <strong>4.1 Tier 1: Direct Booking System Integration.</strong>{" "}
        Where you use a supported booking system (including but not
        limited to Bookteq, Playtomic, and CourtReserve), you may
        authorise Whistll to integrate directly via API.
      </p>
      <p>By authorising integration, you:</p>
      <ul>
        <li>
          (a) grant Whistll permission to pull real-time availability
          from your system;
        </li>
        <li>
          (b) grant Whistll permission to push confirmed bookings to
          your system;
        </li>
        <li>(c) warrant that you have the right to grant this permission;</li>
        <li>
          (d) agree that any availability pulled and bookings pushed
          are subject to the Whistll booking flow; and
        </li>
        <li>
          (e) acknowledge that Whistll is not responsible for errors,
          outages, or discrepancies caused by the third-party booking
          system.
        </li>
      </ul>
      <p>
        <strong>4.2 Tier 2: Direct Availability Management.</strong>{" "}
        If you do not use a supported booking system, you manage your
        availability through the Whistll Venue Dashboard. You are
        responsible for keeping availability, pricing, and court
        information accurate and up to date.
      </p>
      <p>
        <strong>4.3 Accuracy of Information.</strong> You are solely
        responsible for ensuring that all information about your
        venue, including availability, pricing, court configuration,
        and photos, is accurate and current at all times. Whistll is
        not liable for any consequences arising from inaccurate or
        outdated venue information.
      </p>

      <h2>5. Payments &amp; Fees</h2>
      <p>
        <strong>5.1 Whistll Platform Fee.</strong> Whistll charges
        players a platform fee of twenty percent (20%){" "}
        <strong>on top of</strong> your court fee per player. This fee
        is in addition to, and does not reduce, the amount payable to
        you.
      </p>
      <p>
        <strong>Example:</strong> If your court fee is £12 per player,
        the player pays £14.40 total (£12 to you and £2.40 to
        Whistll). You always receive your full court fee.
      </p>
      <p>
        <strong>5.2 Payment Collection.</strong> All player payments
        are collected by Whistll through Stripe at the time of
        booking. Payments are pre-authorised and only captured once a
        game is confirmed.
      </p>
      <p>
        <strong>5.3 Payout Schedule.</strong> Once a game is confirmed
        (i.e. the required number of players has been reached),
        Whistll will initiate a Stripe Connect payout to your verified
        Stripe account for the full court fee. Payout timing is
        determined by Stripe's standard payout schedule, typically
        2–7 business days depending on your jurisdiction.
      </p>
      <p>
        <strong>5.4 Failed or Cancelled Bookings.</strong> If a game
        fails to reach the required player count and is automatically
        cancelled, or is cancelled under the Whistll refund policy, no
        payout is issued for that game.
      </p>
      <p>
        <strong>5.5 Clawback and Refunds.</strong> Where a confirmed
        game is cancelled or a refund is issued to players after
        payout has been made to you (including under the Extenuating
        Circumstances clause of the User Terms), Whistll reserves the
        right to:
      </p>
      <ul>
        <li>(a) deduct the refunded amount from future payouts; or</li>
        <li>
          (b) require you to return the corresponding amount within 14
          days of notice.
        </li>
      </ul>
      <p>
        <strong>5.6 Taxes.</strong> You are solely responsible for
        determining, collecting, and remitting any taxes (including
        VAT, GST, sales tax, or similar) applicable to your court
        fees. Whistll does not calculate, collect, or remit taxes on
        your behalf.
      </p>
      <p>
        <strong>5.7 Currency.</strong> Payouts are made in your local
        currency where supported by Stripe. Currency conversion (where
        applicable) is handled by Stripe subject to their rates and
        fees.
      </p>

      <h2>6. Venue Obligations</h2>
      <p>
        <strong>6.1 Court Availability.</strong> You agree to honour
        all confirmed bookings made through the Platform. Failure to
        make a court available for a confirmed booking may result in:
      </p>
      <ul>
        <li>(a) a refund to affected players;</li>
        <li>(b) clawback of any payout issued for the affected booking;</li>
        <li>(c) reputational or reliability markers on your venue listing; and/or</li>
        <li>(d) suspension or termination of this Agreement.</li>
      </ul>
      <p>
        <strong>6.2 Venue Standards.</strong> You agree to maintain
        your facilities to a reasonable standard, including ensuring:
      </p>
      <ul>
        <li>(a) courts are in safe, playable condition;</li>
        <li>(b) lighting, markings, and equipment are adequate;</li>
        <li>(c) access instructions are clear; and</li>
        <li>
          (d) any stated amenities (changing rooms, parking, etc.) are
          available as described.
        </li>
      </ul>
      <p>
        <strong>6.3 Legal Compliance.</strong> You warrant that you
        hold all licences, permits, insurance, and regulatory
        approvals required to operate your facilities and accept
        bookings.
      </p>
      <p>
        <strong>6.4 Safety &amp; Insurance.</strong> You are
        responsible for the safety of all players using your
        facilities and must maintain adequate public liability
        insurance at all times.
      </p>
      <p>
        <strong>6.5 Non-Circumvention.</strong> Venues may not contact
        Whistll players to arrange bookings outside the Platform for a
        period of twelve (12) months after a player's first booking
        at your venue, where that booking was facilitated through
        Whistll.
      </p>
      <p>
        <strong>6.6 Discrimination.</strong> You agree not to
        discriminate against any player on the basis of race,
        ethnicity, national origin, religion, gender, sexual
        orientation, disability, or any other protected
        characteristic.
      </p>

      <h2>7. Venue-Side Cancellations</h2>
      <p>
        <strong>7.1 Force Majeure.</strong> If you must cancel a
        confirmed booking due to circumstances beyond your reasonable
        control (weather, facility failure, legal closure, etc.), you
        must notify Whistll as soon as reasonably practicable.
        Affected players will be refunded in full, and no clawback
        will apply provided the cancellation is genuine and
        reasonable.
      </p>
      <p>
        <strong>7.2 Operational Cancellations.</strong> Repeated
        non-force-majeure cancellations by a Venue Partner may result
        in reliability markers on your listing, reduced visibility in
        the App, or termination of this Agreement.
      </p>

      <h2>8. Data &amp; Player Information</h2>
      <p>
        <strong>8.1 Limited Data Access.</strong> Whistll shares
        limited player data with you to facilitate bookings, including
        player name and contact details. You may only use this data
        for the purpose of the relevant booking.
      </p>
      <p>
        <strong>8.2 Data Protection.</strong> You agree to process any
        player data received through the Platform in accordance with
        all applicable data protection laws, including UK GDPR, the
        Data (Use and Access) Act 2025, applicable US state privacy
        laws, PIPEDA (Canada), the Australian Privacy Act 1988, and
        the New Zealand Privacy Act 2020.
      </p>
      <p>
        <strong>8.3 Independent Data Controller.</strong> For the
        purposes of data protection law, you act as an independent
        data controller in respect of the player data you receive.
        You are solely responsible for your own data protection
        compliance.
      </p>
      <p>
        <strong>8.4 No Marketing Without Consent.</strong> You may not
        use player data received through the Platform to send
        marketing communications without the player's express
        consent.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        <strong>9.1 Whistll IP.</strong> All intellectual property in
        the Platform, including software, designs, trade marks, and
        branding, remains the property of Whistll.
      </p>
      <p>
        <strong>9.2 Venue IP.</strong> You retain ownership of your
        venue's branding, trade marks, and content (including photos)
        but grant Whistll the licence described in clause 3.3.
      </p>
      <p>
        <strong>9.3 No Misuse.</strong> You may not use the Whistll
        name, logo, or branding for any purpose other than indicating
        your partnership with Whistll, without our prior written
        consent.
      </p>

      <h2>10. Termination</h2>
      <p>
        <strong>10.1 Termination by Venue Partner.</strong> You may
        terminate this Agreement at any time by contacting Whistll at{" "}
        <a
          href="mailto:hello@whistll.com"
          className="underline underline-offset-2 hover:text-foreground"
        >
          hello@whistll.com
        </a>
        . Termination takes effect after all confirmed bookings are
        fulfilled or refunded.
      </p>
      <p>
        <strong>10.2 Termination by Whistll.</strong> Whistll may
        terminate or suspend this Agreement at any time, with or
        without notice, where:
      </p>
      <ul>
        <li>(a) you breach this Agreement;</li>
        <li>(b) you fail to honour confirmed bookings;</li>
        <li>(c) player feedback indicates serious issues with your facility;</li>
        <li>(d) you engage in conduct harmful to the Platform, its users, or Whistll's reputation;</li>
        <li>(e) your Stripe Connect account is suspended or terminated; or</li>
        <li>(f) for any other reason at Whistll's sole discretion.</li>
      </ul>
      <p>
        <strong>10.3 Effect of Termination.</strong> Upon termination:
      </p>
      <ul>
        <li>(a) your courts will be removed from the App;</li>
        <li>
          (b) all confirmed bookings must still be honoured (or
          refunded, at Whistll's discretion);
        </li>
        <li>
          (c) any outstanding payouts will be processed in accordance
          with normal payout cycles; and
        </li>
        <li>
          (d) provisions relating to data protection,
          non-circumvention, indemnification, and liability survive
          termination.
        </li>
      </ul>

      <h2>11. Liability</h2>
      <p>
        <strong>11.1 Mutual Limitation.</strong> To the fullest extent
        permitted by law, neither party's liability to the other under
        or in connection with this Agreement shall exceed the total
        amount of Whistll platform fees paid in the 12 months
        preceding the claim.
      </p>
      <p>
        <strong>11.2 No Indirect Loss.</strong> Neither party shall be
        liable for indirect, incidental, consequential, special,
        exemplary, or punitive damages, including loss of profits,
        revenue, data, goodwill, or business opportunity.
      </p>
      <p>
        <strong>11.3 No Exclusion Where Not Lawful.</strong> Nothing
        in this Agreement excludes liability for death or personal
        injury caused by negligence, fraud, or any liability that
        cannot lawfully be excluded.
      </p>
      <p>
        <strong>11.4 Venue Partner Responsibility.</strong> You
        acknowledge that Whistll is not responsible for:
      </p>
      <ul>
        <li>(a) the condition or operation of your facilities;</li>
        <li>(b) injuries, accidents, or damages occurring at your facilities;</li>
        <li>(c) disputes between players at your facilities; or</li>
        <li>(d) your compliance with laws and regulations applicable to your business.</li>
      </ul>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Whistll, its
        affiliates, directors, officers, employees, and partners from
        any claims, demands, losses, liabilities, and expenses
        (including reasonable legal fees) arising out of:
      </p>
      <ul>
        <li>(a) the operation of your facilities;</li>
        <li>(b) any injury, accident, or damage occurring at your facilities;</li>
        <li>(c) your breach of this Agreement;</li>
        <li>(d) your violation of any law; or</li>
        <li>(e) any third-party claim relating to your services.</li>
      </ul>

      <h2>13. Changes to This Agreement</h2>
      <p>
        Whistll may update this Agreement from time to time. Material
        changes will be notified to you in advance by email or through
        the Venue Dashboard. Continued use of the Platform after
        changes take effect constitutes acceptance. If you do not
        agree, you may terminate under clause 10.1.
      </p>

      <h2>14. Governing Law</h2>
      <p>
        If your venue is located in the United Kingdom, this Agreement
        is governed by the laws of England and Wales and subject to
        the exclusive jurisdiction of its courts.
      </p>
      <p>
        If your venue is located in the United States, this Agreement
        is governed by the laws of the State of Delaware, and disputes
        shall be brought in the state or federal courts located in
        Delaware.
      </p>
      <p>
        If your venue is located in Canada, this Agreement is governed
        by the laws of the Province of Ontario.
      </p>
      <p>
        If your venue is located in Australia, this Agreement is
        governed by the laws of New South Wales.
      </p>
      <p>
        If your venue is located in New Zealand, this Agreement is
        governed by the laws of New Zealand.
      </p>

      <h2>15. Entire Agreement</h2>
      <p>
        This Agreement, together with the Whistll User Terms and
        Privacy Policy (to the extent applicable), constitutes the
        entire agreement between you and Whistll regarding your venue
        partnership.
      </p>

      <h2>16. Contact</h2>
      <p>For all matters relating to this Agreement, contact:</p>
      <p>
        <strong>Email:</strong>{" "}
        <a
          href="mailto:hello@whistll.com"
          className="underline underline-offset-2 hover:text-foreground"
        >
          hello@whistll.com
        </a>
        <br />
        <strong>Address:</strong> 71-75 Shelton Street, Covent Garden,
        London, WC2H 9JQ
        <br />
        <strong>Company number:</strong> 17172530
      </p>
    </LegalPlaceholder>
  );
}
